import { useState, forwardRef, useImperativeHandle, useRef, useCallback, useEffect } from 'react';
import Select from 'react-select';
import Modal from '../common/modal.js';
import Api from '../../../../component/Api.js';

const STATUS_OPTIONS = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Reverted', label: 'Reverted' },
    { value: 'Absent', label: 'Absent' },
];

const FilterModal = forwardRef(({ onApplyFilter, initialFilters = {} }, ref) => {
    const { http } = Api();

    const modalRef = useRef();

    const disciplinesCacheRef = useRef({
        data: [],
        isFullyLoaded: false,
        lastPage: 1,
        searchCache: {}
    });

    const coursesCacheRef = useRef({
        data: {},
        isFullyLoaded: {},
        lastPage: {},
        searchCache: {}
    });

    const debounceTimerRef = useRef(null);
    const loadingRequestRef = useRef({ discipline: null, course: null });

    const [filters, setFilters] = useState({
        course: initialFilters.course || '',
        discipline: initialFilters.discipline || '',
        status: initialFilters.status || '',
        search: initialFilters.search || '',
    });

    const [disciplines, setDisciplines] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loadingDisciplines, setLoadingDisciplines] = useState(false);
    const [loadingCourses, setLoadingCourses] = useState(false);

    const [disciplinePage, setDisciplinePage] = useState(1);
    const [coursePage, setCoursePage] = useState(1);
    const [hasMoreDisciplines, setHasMoreDisciplines] = useState(true);
    const [hasMoreCourses, setHasMoreCourses] = useState(true);

    const [disciplineInputValue, setDisciplineInputValue] = useState('');
    const [courseInputValue, setCourseInputValue] = useState('');

    useEffect(() => {
        setFilters({
            course: initialFilters.course || '',
            discipline: initialFilters.discipline || '',
            status: initialFilters.status || '',
            search: initialFilters.search || '',
        });
    }, [initialFilters.course, initialFilters.discipline, initialFilters.status, initialFilters.search]);

    useImperativeHandle(ref, () => ({
        open() { modalRef.current?.open(); },
        close() { modalRef.current?.close(); }
    }));

    const handleSearchChange = (e) => {
        setFilters(prev => ({ ...prev, search: e.target.value }));
    };

    const loadDisciplines = async (page = 1, search = '') => {
        const cacheKey = search || 'default';

        if (search && disciplinesCacheRef.current.searchCache[cacheKey]) {
            const cached = disciplinesCacheRef.current.searchCache[cacheKey];
            setDisciplines(cached.data);
            setHasMoreDisciplines(!cached.isFullyLoaded);
            setDisciplinePage(cached.lastPage);
            return;
        }

        if (!search && page > 1 && disciplinesCacheRef.current.isFullyLoaded) return;

        const requestId = Math.random();
        loadingRequestRef.current.discipline = requestId;

        try {
            setLoadingDisciplines(true);
            const response = await http.get('/is/disciplineFilter', {
                params: { page, per_page: 20, search }
            });

            if (loadingRequestRef.current.discipline !== requestId) return;

            const newOptions = response.data.data.map(item => ({
                value: item.id,
                label: item.discipline_name
            }));

            setDisciplines(prev => {
                if (page === 1) return newOptions;
                const existingIds = new Set(prev.map(d => d.value));
                return [...prev, ...newOptions.filter(opt => !existingIds.has(opt.value))];
            });

            const isFullyLoaded = response.data.current_page >= response.data.last_page;
            setHasMoreDisciplines(!isFullyLoaded);
            setDisciplinePage(response.data.current_page);

            if (!search) {
                setDisciplines(current => {
                    disciplinesCacheRef.current = {
                        data: current,
                        isFullyLoaded,
                        lastPage: response.data.current_page,
                        searchCache: disciplinesCacheRef.current.searchCache
                    };
                    return current;
                });
            } else {
                setDisciplines(current => {
                    disciplinesCacheRef.current.searchCache[cacheKey] = {
                        data: current,
                        isFullyLoaded,
                        lastPage: response.data.current_page
                    };
                    return current;
                });
            }
        } catch (error) {
            console.error('Error loading disciplines:', error);
        } finally {
            if (loadingRequestRef.current.discipline === requestId) {
                setLoadingDisciplines(false);
            }
        }
    };

    const loadCourses = async (page = 1, search = '', disciplineId = '') => {
        const cacheKey = `${disciplineId || 'all'}_${search || 'default'}`;

        if (search && coursesCacheRef.current.searchCache[cacheKey]) {
            const cached = coursesCacheRef.current.searchCache[cacheKey];
            setCourses(cached.data);
            setHasMoreCourses(!cached.isFullyLoaded);
            setCoursePage(cached.lastPage);
            return;
        }

        const dataKey = disciplineId || '';
        if (!search && page > 1 && coursesCacheRef.current.isFullyLoaded[dataKey]) return;

        const requestId = Math.random();
        loadingRequestRef.current.course = requestId;

        try {
            setLoadingCourses(true);
            const response = await http.get('/is/courseFilter', {
                params: {
                    page,
                    per_page: 20,
                    search,
                    ...(disciplineId && { discipline: disciplineId })
                }
            });

            if (loadingRequestRef.current.course !== requestId) return;

            const newOptions = response.data.data.map(item => ({
                value: item.id,
                label: item.course
            }));

            setCourses(prev => {
                if (page === 1) return newOptions;
                const existingIds = new Set(prev.map(c => c.value));
                return [...prev, ...newOptions.filter(opt => !existingIds.has(opt.value))];
            });

            const isFullyLoaded = response.data.current_page >= response.data.last_page;
            setHasMoreCourses(!isFullyLoaded);
            setCoursePage(response.data.current_page);

            if (!search) {
                setCourses(current => {
                    coursesCacheRef.current.data[dataKey] = current;
                    coursesCacheRef.current.isFullyLoaded[dataKey] = isFullyLoaded;
                    coursesCacheRef.current.lastPage[dataKey] = response.data.current_page;
                    return current;
                });
            } else {
                setCourses(current => {
                    coursesCacheRef.current.searchCache[cacheKey] = {
                        data: current,
                        isFullyLoaded,
                        lastPage: response.data.current_page
                    };
                    return current;
                });
            }
        } catch (error) {
            console.error('Error loading courses:', error);
        } finally {
            if (loadingRequestRef.current.course === requestId) {
                setLoadingCourses(false);
            }
        }
    };

    const handleDisciplineMenuOpen = useCallback(() => {
        if (disciplinesCacheRef.current.data.length > 0) {
            setDisciplines(disciplinesCacheRef.current.data);
            setDisciplinePage(disciplinesCacheRef.current.lastPage);
            setHasMoreDisciplines(!disciplinesCacheRef.current.isFullyLoaded);
        } else {
            loadDisciplines(1, '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCourseMenuOpen = useCallback(() => {
        const cacheKey = filters.discipline || '';
        if (coursesCacheRef.current.data[cacheKey] !== undefined) {
            setCourses(coursesCacheRef.current.data[cacheKey]);
            setCoursePage(coursesCacheRef.current.lastPage[cacheKey] || 1);
            setHasMoreCourses(!coursesCacheRef.current.isFullyLoaded[cacheKey]);
        } else {
            loadCourses(1, '', filters.discipline);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.discipline]);

    const handleDisciplineChange = (selectedOption) => {
        const newDisciplineId = selectedOption?.value || '';
        setFilters(prev => ({ ...prev, discipline: newDisciplineId, course: '' }));

        const cacheKey = newDisciplineId || '';
        if (coursesCacheRef.current.data[cacheKey] !== undefined) {
            setCourses(coursesCacheRef.current.data[cacheKey]);
            setCoursePage(coursesCacheRef.current.lastPage[cacheKey] || 1);
            setHasMoreCourses(!coursesCacheRef.current.isFullyLoaded[cacheKey]);
        } else {
            setCourses([]);
        }

        setCourseInputValue('');
        setDisciplineInputValue('');
    };

    const handleCourseChange = (selectedOption) => {
        setFilters(prev => ({ ...prev, course: selectedOption?.value || '' }));
        setCourseInputValue('');
    };

    const handleStatusChange = (selectedOption) => {
        setFilters(prev => ({ ...prev, status: selectedOption?.value ?? '' }));
    };

    const handleDisciplineInputChange = useCallback((inputValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
            setDisciplineInputValue(inputValue);
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = setTimeout(() => {
                setDisciplinePage(1);
                loadDisciplines(1, inputValue);
            }, 500);
        }
        //eslint-disable-next-line
    }, []);

    const handleCourseInputChange = useCallback((inputValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
            setCourseInputValue(inputValue);
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            const currentDiscipline = filters.discipline;
            debounceTimerRef.current = setTimeout(() => {
                setCoursePage(1);
                loadCourses(1, inputValue, currentDiscipline);
            }, 500);
        }
        //eslint-disable-next-line
    }, [filters.discipline]);

    const handleDisciplineMenuScrollToBottom = useCallback(() => {
        if (hasMoreDisciplines && !loadingDisciplines) {
            loadDisciplines(disciplinePage + 1, disciplineInputValue);
        }
        //eslint-disable-next-line
    }, [hasMoreDisciplines, loadingDisciplines, disciplinePage, disciplineInputValue]);

    const handleCourseMenuScrollToBottom = useCallback(() => {
        if (hasMoreCourses && !loadingCourses) {
            loadCourses(coursePage + 1, courseInputValue, filters.discipline);
        }
        //eslint-disable-next-line
    }, [hasMoreCourses, loadingCourses, coursePage, courseInputValue, filters.discipline]);

    const handleApplyFilter = () => {
        onApplyFilter(filters);
        modalRef.current?.close();
    };

    const handleCancel = () => {
        setFilters({
            course: initialFilters.course || '',
            discipline: initialFilters.discipline || '',
            status: initialFilters.status || '',
            search: initialFilters.search || '',
        });
        modalRef.current?.close();
    };

    const handleClearFilters = () => {
        setFilters({ course: '', discipline: '', status: '', search: '' });
        setDisciplineInputValue('');
        setCourseInputValue('');

        const cacheKey = '';
        if (coursesCacheRef.current.data[cacheKey] !== undefined) {
            setCourses(coursesCacheRef.current.data[cacheKey]);
            setCoursePage(coursesCacheRef.current.lastPage[cacheKey] || 1);
            setHasMoreCourses(!coursesCacheRef.current.isFullyLoaded[cacheKey]);
        } else {
            setCourses([]);
        }
    };

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    const selectedDiscipline = disciplines.find(d => d.value === filters.discipline) || null;
    const selectedCourse = courses.find(c => c.value === filters.course) || null;
    const selectedStatus = STATUS_OPTIONS.find(s => s.value === filters.status) || null;

    const selectStyles = {
        control: (base) => ({ ...base, fontSize: 'inherit' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    const hasAnyFilter = filters.discipline || filters.course || filters.status || filters.search;

    return (
        <Modal
            ref={modalRef}
            title="Filter"
            footer={
                <div className="d-flex justify-content-between align-items-center w-100">
                    <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0"
                        onClick={handleClearFilters}
                        disabled={!hasAnyFilter}
                    >
                        Clear Filters
                    </button>
                    <div>
                        <button type="button" className="btn btn-light me-2" onClick={handleCancel}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleApplyFilter}>
                            Apply Filter
                        </button>
                    </div>
                </div>
            }
        >
            <div className="mb-3">
                <label htmlFor="searchFilter" className="form-label">Search</label>
                <input
                    type="text"
                    id="searchFilter"
                    className="form-control"
                    placeholder="Search students..."
                    value={filters.search}
                    onChange={handleSearchChange}
                />
            </div>

            <div className="mb-3">
                <label htmlFor="disciplineFilter" className="form-label">Discipline</label>
                <Select
                    id="disciplineFilter"
                    options={disciplines}
                    value={selectedDiscipline}
                    onChange={handleDisciplineChange}
                    onMenuOpen={handleDisciplineMenuOpen}
                    onInputChange={handleDisciplineInputChange}
                    inputValue={disciplineInputValue}
                    onMenuScrollToBottom={handleDisciplineMenuScrollToBottom}
                    isLoading={loadingDisciplines}
                    isClearable
                    placeholder="Select Discipline"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                />
            </div>

            <div className="mb-3">
                <label htmlFor="courseFilter" className="form-label">Course</label>
                <Select
                    id="courseFilter"
                    options={courses}
                    value={selectedCourse}
                    onChange={handleCourseChange}
                    onMenuOpen={handleCourseMenuOpen}
                    onInputChange={handleCourseInputChange}
                    inputValue={courseInputValue}
                    onMenuScrollToBottom={handleCourseMenuScrollToBottom}
                    isLoading={loadingCourses}
                    isClearable
                    placeholder="Select Course"
                    noOptionsMessage={() => "No courses found"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                />
            </div>

            <div className="mb-3">
                <label htmlFor="statusFilter" className="form-label">Status</label>
                <Select
                    id="statusFilter"
                    options={STATUS_OPTIONS}
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    isClearable
                    placeholder="Select Status"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectStyles}
                />
            </div>
        </Modal>
    );
});

FilterModal.displayName = 'FilterModal';

export default FilterModal;